<template>
  <button
    class="text-b2 relative mb-1 flex h-20 w-full items-center justify-center rounded-[15px] text-center"
    @click="clickHandler($event)"
    @keyup.enter="clickHandler($event)"
    tabindex="0"
    :class="{
      'bg-turquoise-10 text-dark-turquoise hover:bg-turquoise-20': status == 'available',
      'bg-gray-light text-gray': status == 'unavailable' || status == 'passed-booking',
      'bg-success-10 text-black ring-1 ring-green': status == 'booked',
    }"
    :disabled="status == 'unavailable'"
  >
    <IconGreenCheckmark v-if="status == 'booked'" class="absolute right-1 top-1" />
    {{ formatTime(timeslot.startTime) }}-{{ formatTime(timeslot.endTime) }}
  </button>

  <!-- Vill du boka ....  ? -->
  <Modal
    v-if="showModal.book"
    cancelText="Nej, avbryt"
    confirmText="Ja, boka"
    size="sm"
    loadingText="Bokar"
    :loading="isLoading"
    v-on:confirm="
      confirmBooking().then(() => {
        this.showModal.book = false;
      })
    "
    v-on:cancel="showModal.book = false"
  >
    <p class="text-t5 text-left">
      Vill du boka
      <strong>{{ startTimeFormated }} - {{ endTimeFormated }}, {{ dateFormated }}</strong>
      ?
    </p>

    <div v-if="bookingRules" class="mb-4 mt-2 rounded-[15px] bg-gray-10 p-4">
      <p>{{ bookingRules }}</p>
      <a v-if="bookingRulesLink" v-bind:href="bookingRulesLink" class="underline">Läs mer</a>
    </div>
  </Modal>

  <!-- Tack för att du bokade -->
  <Modal v-if="showModal.thanx" cancelText="Stäng" v-on:cancel="showModal.thanx = false">
    <p class="text-t5 text-center">Tack för din bokning</p>
  </Modal>

  <!-- När man klickat på egen bokad tid -->
  <Modal v-if="showModal.booked" cancelText="Stäng" v-on:cancel="showModal.booked = false">
    <template #footer>
      <Button
        size="large"
        variant="danger"
        v-on:click="
          cancelBooking($event);
          showModal.booked = false;
        "
      >
        Avboka tiden
      </Button>
    </template>
    <p class="text-t5 text-center">
      Du har bokat
      <strong>{{ startTimeFormated }} - {{ endTimeFormated }}, {{ dateFormated }}</strong>
    </p>
  </Modal>

  <!-- Bekräfta att man vill avboka -->
  <Modal
    v-if="showModal.unbook"
    confirmText="Ja"
    cancelText="Avbryt"
    v-on:confirm="
      cancelBooking($event);
      showModal.unbook = false;
    "
    v-on:cancel="showModal.unbook = false"
  >
    <p class="text-t5 text-center">
      Vill du avboka
      <strong>{{ startTimeFormated }} - {{ endTimeFormated }}, {{ dateFormated }}</strong>
      ?
    </p>
  </Modal>

  <!-- Fel vid "Du har nåt max antal bokningar per tvättstuga" -->
  <Modal
    v-if="showModal.cantUnbookStartedTime"
    cancelText="Stäng"
    v-on:cancel="showModal.cantUnbookStartedTime = false"
  >
    <p class="text-t5 text-center">Du kan inte avboka en tvätttid som pågår eller passerat</p>
  </Modal>

  <!-- Visas när man avbokat tid -->
  <Modal v-if="showModal.unbooked" cancelText="Stäng" v-on:cancel="showModal.unbooked = false">
    <p class="text-t5 text-center">Din tid är avbokad</p>
  </Modal>

  <!-- Fel vid request modal -->
  <Modal v-if="showModal.error" cancelText="Stäng" v-on:cancel="showModal.error = false">
    <p class="text-t5 text-center">Något gick fel, försök igen</p>
  </Modal>

  <!-- Fel vid "Du har nåt max antal bokningar per tvättstuga" -->
  <Modal
    v-if="showModal.reachedMaxBookingsLimit"
    cancelText="Stäng"
    v-on:cancel="showModal.reachedMaxBookingsLimit = false"
  >
    <p class="text-t5 text-center">Du har nåt max antal bokningar per tvättstuga</p>
  </Modal>

  <!-- Fel vid "Du har nåt max antal bokningar per tvättstuga" -->
  <Modal
    v-if="showModal.tooManyDelicates"
    cancelText="Stäng"
    v-on:cancel="showModal.tooManyDelicates = false"
  >
    <p class="text-center">
      Din bokning kunde inte genomföras. Antingen har du förbrukat dina tider för aktuell period,
      eller så har du redan en aktiv tid bokad
    </p>
  </Modal>

  <!-- Fel vid "Du har nåt max antal bokningar per tvättstuga" -->
  <Modal
    v-if="showModal.tooManyRoughWashings"
    cancelText="Stäng"
    v-on:cancel="showModal.tooManyRoughWashings = false"
  >
    <p class="text-center">
      Din bokning kunde inte genomföras. Antingen har du förbrukat dina tider för aktuell period,
      eller så har du redan en aktiv tid bokad
    </p>
  </Modal>
</template>

<script>
import Modal from '@/components/ui/modal';
import Button from '@/components/ui/button';
import IconGreenCheckmark from '@/components/icons/IconGreenCheckmark';
import dayjs from 'dayjs';
import { mapGetters } from 'vuex';
import { groupBy, findIndex } from 'lodash';

export default {
  name: 'LaundryTimeslot',
  components: { Modal, Button, IconGreenCheckmark },
  props: {
    timeslot: {
      type: Object,
      required: true,
    },

    bookingRules: {
      type: String,
      required: false
    },

    bookingRulesLink: {
      type: String,
      required: false
    }
  },
  data: function () {
    return {
      isLoading: false,
      showModal: {
        // Bekräfta att man vill boka
        book: false,

        // Tack för din bokning
        thanx: false,

        // När man klickar på en bokad tid
        booked: false,

        // Vill du avboka tiden?
        unbook: false,

        // Visa "Du har avbokat..."
        unbooked: false,

        // Du kan inte avboka en pågående eller passerad tvättid
        cantUnbookStartedTime: false,

        // Visa "Du har nåt max antal bokningar per tvättstuga"
        reachedMaxBookingsLimit: false,

        // Visa "Tvärr, något blev fel"
        error: false,

        tooManyDelicates: false,

        tooManyRoughWashings: false,
      },
    };
  },
  computed: {
    ...mapGetters({
      selectedGroupName: 'selectedGroupName',
      bookings: 'bookings',
    }),
    reachedMaxBookingsLimit() {
      const MAX_BOOKINGS_PER_MONTH = 5;

      // grupperar bokningar på månad
      let groupedByYearMonth = groupBy(this.$store.getters.bookingsOnSelectedGroup, (x) => {
        return dayjs(x.StartTimeStamp).format('YYYY-MM');
      });

      let selectedYearMonth = dayjs(this.timeslot.date).format('YYYY-MM');

      // Finns det redan max antal bokningar denna månad?
      return (
        groupedByYearMonth[selectedYearMonth] !== undefined &&
        groupedByYearMonth[selectedYearMonth].length >= MAX_BOOKINGS_PER_MONTH
      );
    },
    startTimeFormated: function () {
      return dayjs('2017-01-01T' + this.timeslot.startTime).format('HH:mm');
    },
    endTimeFormated: function () {
      return dayjs('2017-01-01T' + this.timeslot.endTime).format('HH:mm');
    },
    dateFormated: function () {
      return (
        dayjs(this.timeslot.date).format('dddd') +
        ' den ' +
        ' ' +
        dayjs(this.timeslot.date).format('DD MMMM')
      );
    },
    status: function () {
      if (this.timeslot.bookedByMe) {
        if (
          findIndex(this.bookings, {
            BookingId: this.timeslot.bookingDetails.BookingId,
            IsUsed: true,
          }) >= 0
        ) {
          return 'passed-booking';
        }

        return 'booked';
      }

      if (this.timeslot.bookable) return 'available';

      if (!this.timeslot.bookable) return 'unavailable';
    },
    startTimeHasPast() {
      return dayjs(this.timeslot.date + ' ' + this.timeslot.startTime).isBefore(new Date());
    },
  },
  methods: {
    formatTime(time) {
      return dayjs(`2017-01-01 ${time}`).format('HH:mm');
    },
    bookAndCloseModal() {
      this.confirmBooking().then(() => {
        this.showModal.book = false;
      });
    },
    clickHandler: function (e) {
      if (this.status == 'available') {
        if (this.reachedMaxBookingsLimit) {
          this.showModal.reachedMaxBookingsLimit = true;
        } else {
          this.showModal.book = true;
        }
      }

      if (this.status == 'booked') {
        this.showModal.booked = true;
      }

      e.preventDefault();
      e.stopPropagation();
    },
    confirmBooking: async function () {
      this.isLoading = true;
      // const loader = gavlegardarna.loader.show(document.querySelector('body'), true, true);
      return this.$store
        .dispatch('bookLaundry', {
          startTime: dayjs(this.timeslot.date + 'T' + this.timeslot.startTime).format(
            'YYYY-MM-DDTHH:mm',
          ),
          bookingLength: this.timeslot.bookingLength,
        })
        .then((response) => {
          this.isLoading = false;

          this.showModal.thanx = true;
          // gavlegardarna.loader.hide(loader, true);
        })
        .catch((error) => {
          if (error.message == 'tooManyActive') {
            if (this.selectedGroupName == 'Grovtvätt') {
              this.showModal.tooManyRoughWashings = true;
            }
            if (this.selectedGroupName != 'Grovtvätt') {
              this.showModal.tooManyDelicates = true;
            }
          }
          // om "Conflict", visa "specifikt felmeddelnade"
          else if (error.message.search(/409/) > -1) {
            this.showModal.reachedMaxBookingsLimit = true;
          } else {
            this.showModal.error = true;
          }
          // gavlegardarna.loader.hide(loader, true);
        });
    },
    cancelBooking: function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (this.startTimeHasPast) {
        this.showModal.cantUnbookStartedTime = true;
        return;
      }
      // const loader = gavlegardarna.loader.show(document.querySelector('body'), true, true);
      return this.$store
        .dispatch('cancelBooking', this.timeslot.bookingDetails.BookingId)
        .then(() => {
          this.showModal.unbooked = true;
          // gavlegardarna.loader.hide(loader, true);
        })
        .catch((error) => {
          console.log(error);
          this.showModal.error = true;
          // gavlegardarna.loader.hide(loader, true);
        });
    },
  },
};
</script>
